import { atom } from "nanostores";

export class RefreshCancelled extends Error {
    constructor() { super("Refresh cancelled"); }
}

export const isRefreshCancelled = (error: unknown) => error instanceof RefreshCancelled;

export interface RefreshState<T> {
    data: T;
    loaded: boolean;
    refreshing: boolean;
    error: string;
    lastSuccessAt: number;
    stale: boolean;
}

export function createRefreshResource<T>(
    loader: (signal: AbortSignal) => Promise<T>,
    initial: T,
    getSession: () => number = () => 0,
) {
    const empty = (): RefreshState<T> => ({
        data: initial, loaded: false, refreshing: false, error: "", lastSuccessAt: 0, stale: true,
    });
    const $state = atom(empty());
    let version = 0;
    let controller: AbortController | undefined;
    let pending: Promise<T> | undefined;
    const invalidate = () => {
        version++;
        controller?.abort(new RefreshCancelled());
        controller = undefined;
        pending = undefined;
        $state.set({ ...$state.get(), stale: true, refreshing: false });
    };
    const reset = () => {
        invalidate();
        $state.set(empty());
    };
    const refresh = (): Promise<T> => {
        if (pending) return pending;
        const generation = version;
        const session = getSession();
        const active = new AbortController();
        controller = active;
        const current = () => generation === version && session === getSession();
        $state.set({ ...$state.get(), refreshing: true, error: "" });
        let abortListener: () => void;
        const aborted = new Promise<never>((_, reject) => {
            abortListener = () => reject(active.signal.reason);
            active.signal.addEventListener("abort", abortListener, { once: true });
        });
        const timer = setTimeout(() => active.abort(new Error("Request timed out (20s)")), 20_000);
        pending = Promise.race([Promise.resolve().then(() => loader(active.signal)), aborted])
            .then(data => {
                if (!current() || active.signal.aborted) throw new RefreshCancelled();
                $state.set({ data, loaded: true, refreshing: false, error: "", lastSuccessAt: Date.now(), stale: false });
                return data;
            })
            .catch(error => {
                if (!current()) throw new RefreshCancelled();
                if (!isRefreshCancelled(error)) {
                    $state.set({ ...$state.get(), refreshing: false, stale: true,
                        error: error instanceof Error ? error.message : String(error) });
                }
                throw error;
            })
            .finally(() => {
                clearTimeout(timer);
                active.signal.removeEventListener("abort", abortListener!);
                if (current()) {
                    pending = undefined;
                    controller = undefined;
                }
            });
        return pending;
    };
    const ensureLoaded = () => {
        const state = $state.get();
        return state.loaded && !state.stale ? Promise.resolve(state.data) : refresh();
    };
    return { $state, refresh, ensureLoaded, invalidate, reset };
}

export type RefreshResource<T> = ReturnType<typeof createRefreshResource<T>>;

/** Wait for every part so a partial failure does not hide successful updates. */
export async function refreshParts(parts: Record<string, Promise<unknown>>) {
    const entries = Object.entries(parts);
    const results = await Promise.allSettled(entries.map(([, promise]) => promise));
    const failures = results.flatMap((result, i) =>
        result.status === "rejected" && !isRefreshCancelled(result.reason)
            ? [`${entries[i][0]}: ${result.reason instanceof Error ? result.reason.message : String(result.reason)}`]
            : []);
    if (failures.length) throw new Error(failures.join("; "));
}

/** A local request owns its result only until the query or session changes. */
export function createRequestScope(getSession: () => number = () => 0) {
    let sequence = 0;
    let disposed = false;
    let controller: AbortController | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cancel = () => {
        sequence++;
        controller?.abort(new RefreshCancelled());
        clearTimeout(timer);
    };
    const begin = () => {
        if (disposed) throw new RefreshCancelled();
        cancel();
        const seq = sequence;
        const session = getSession();
        const active = new AbortController();
        controller = active;
        timer = setTimeout(() => active.abort(new Error("Request timed out (20s)")), 20_000);
        const current = () => seq === sequence && session === getSession();
        return {
            signal: active.signal,
            current,
            finish: () => { if (current()) clearTimeout(timer); },
            check: () => {
                if (!current()) throw new RefreshCancelled();
                if (active.signal.aborted) throw active.signal.reason;
            },
        };
    };
    return { begin, cancel, dispose: () => { disposed = true; cancel(); } };
}
